import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Transaction, Stats, SortField, SortDirection, DateRange, StatusFilterValue } from '../types/dashboard';
import { User } from '../types';
import { getRowDistributionPercentage } from '../utils/retrocessions';

export function useTransactions(
  user: User | null, 
  searchTerm: string,
  dateRange: DateRange,
  statusFilter: StatusFilterValue,
  selectedAgencies: string[] = []
) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<SortField>('property_number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [stats, setStats] = useState<Stats>({
    totalTransactions: 0,
    totalSales: 0,
    totalFees: 0,
    totalNetFees: 0,
    agencyCount: 0,
    potentialCount: 0,
    compromisCount: 0,
    acteCount: 0,
    encaisseCount: 0,
    clotureCount: 0
  });

  const userAgencies = user?.agency ? user.agency.split(',').map(a => a.trim()) : [];
  const isAdmin = user?.role === 'admin';
  const isDirector = user?.role === 'direction';
  const isReadOnly = user?.role === 'lecture';

  const loadTransactions = async () => {
    try {
      console.log('Loading transactions with params:', {
        userAgencies,
        isAdmin,
        isDirector,
        dateRange,
        selectedAgencies
      });

      // Load transactions in two queries: one for potentials and one for others
      const [potentialResponse, otherResponse] = await Promise.all([
        // Query for potential transactions (no date filter)
        supabase
          .from('transactions')
          .select(`
            id,
            property_number,
            seller,
            buyer,
            sale_price,
            fees_with_tax,
            fees_without_tax,
            mandate_type,
            property_origin,
            status,
            compromise_date,
            expected_acte_date,
            regul,
            commentaire_regul,
            transaction_agencies (
              agency
            ),
            transaction_negotiators (
              negotiator:negotiators (
                id,
                firstname,
                lastname
              ),
              role,
              percentage,
              agency
            ),
            transaction_assistants (
              assistant:assistants (
                id,
                firstname,
                lastname
              ),
              role
            ),
            transaction_retrocessions (
              id,
              type,
              amount,
              distribution_type,
              distribution_percentages,
              primary_negotiator_id,
              secondary_negotiator_id,
              tertiary_negotiator_id,
              quaternary_negotiator_id
            )
          `)
          .eq('status', 'potentiel'),

        // Query for other transactions (with date filter)
        supabase
          .from('transactions')
          .select(`
            id,
            property_number,
            seller,
            buyer,
            sale_price,
            fees_with_tax,
            fees_without_tax,
            mandate_type,
            property_origin,
            status,
            compromise_date,
            expected_acte_date,
            regul,
            commentaire_regul,
            transaction_agencies (
              agency
            ),
            transaction_negotiators (
              negotiator:negotiators (
                id,
                firstname,
                lastname
              ),
              role,
              percentage,
              agency
            ),
            transaction_assistants (
              assistant:assistants (
                id,
                firstname,
                lastname
              ),
              role
            ),
            transaction_retrocessions (
              id,
              type,
              amount,
              distribution_type,
              distribution_percentages,
              primary_negotiator_id,
              secondary_negotiator_id,
              tertiary_negotiator_id,
              quaternary_negotiator_id
            )
          `)
          .neq('status', 'potentiel')
          .gte('compromise_date', dateRange.startDate)
          .lte('compromise_date', dateRange.endDate)
      ]);

      if (potentialResponse.error) throw potentialResponse.error;
      if (otherResponse.error) throw otherResponse.error;

      // Combine both results
      const allData = [...(potentialResponse.data || []), ...(otherResponse.data || [])];

      // Filtrer les transactions selon le rôle de l'utilisateur
      // Le profil lecture seule n'est rattaché à aucune agence : il voit tout
      const filteredData = isAdmin || isReadOnly
        ? allData 
        : allData.filter(t => {
            if (isDirector) {
              // Pour un directeur, montrer toutes les transactions de ses agences
              return t.transaction_agencies.some(ta => userAgencies.includes(ta.agency));
            } else {
              // Pour un utilisateur normal, montrer uniquement les transactions de son agence
              return t.transaction_agencies.some(ta => userAgencies.includes(ta.agency));
            }
          });

      const formattedData = filteredData.map(t => {
        console.log('\nProcessing transaction:', t.property_number);

        // Calculer les honoraires nets HT pour chaque négociateur
        const negotiatorNetFees = t.transaction_negotiators.map(tn => {
          // UTILISATION DE transaction_negotiators.agency (toujours renseigné)
          const negotiatorAgency = tn.agency;

          // Vérifier si le négociateur appartient aux agences sélectionnées
          const isFromSelectedAgencies = selectedAgencies.length === 0 || 
            selectedAgencies.includes(negotiatorAgency);

          // Si le négociateur n'est pas des agences sélectionnées, retourner 0
          if (!isFromSelectedAgencies) {
            return {
              id: tn.negotiator.id,
              name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
              role: tn.role,
              percentage: tn.percentage,
              agency: tn.agency || '',
              netFees: 0
            };
          }

          // Calculer les honoraires bruts du négociateur
          const baseAmount = (t.fees_without_tax * tn.percentage) / 100;
          console.log('Negotiator base amount:', {
            negotiator: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            percentage: tn.percentage,
            baseAmount
          });

          // Calculer les rétrocessions pour ce négociateur
          let retrocessionsAmount = 0;
          t.transaction_retrocessions?.forEach(r => {
            let retroShare = 0;

            if (r.distribution_type === 'single' && r.primary_negotiator_id === tn.negotiator.id) {
              retroShare = r.amount;
            } else if (r.distribution_type === 'equal') {
              const participants = [
                r.primary_negotiator_id,
                r.secondary_negotiator_id,
                r.tertiary_negotiator_id,
                r.quaternary_negotiator_id
              ].filter(Boolean);
              if (participants.includes(tn.negotiator.id)) {
                retroShare = r.amount / participants.length;
              }
            } else if (r.distribution_type === 'custom' && r.distribution_percentages) {
              // Répartition indexée par négociateur + agence : un négociateur présent
              // dans deux agences a une part distincte par ligne
              const percentage = getRowDistributionPercentage(
                r.distribution_percentages,
                tn.negotiator.id,
                tn.agency
              );
              if (percentage) {
                retroShare = (r.amount * percentage) / 100;
              }
            }

            console.log('Retrocession calculation:', {
              type: r.type,
              amount: r.amount,
              distribution_type: r.distribution_type,
              retroShare
            });

            retrocessionsAmount += retroShare;
          });

          console.log('Final negotiator amounts:', {
            baseAmount,
            retrocessionsAmount,
            netAmount: baseAmount - retrocessionsAmount
          });

          return {
            id: tn.negotiator.id,
            name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
            role: tn.role,
            percentage: tn.percentage,
            agency: tn.agency || '',
            netFees: baseAmount - retrocessionsAmount
          };
        });

        // Calculer les honoraires nets totaux pour les négociateurs des agences sélectionnées
        const agencyNetFees = negotiatorNetFees
          .reduce((sum, n) => sum + n.netFees, 0);

        console.log('Total net fees for selected agencies:', agencyNetFees);

        return {
          ...t,
          agencies: t.transaction_agencies.map(ta => ta.agency),
          negotiators: negotiatorNetFees.map(({ id, name, role, percentage }) => ({
            id,
            name,
            role,
            percentage
          })),
          assistants: t.transaction_assistants?.map(ta => ({
            id: ta.assistant.id,
            name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
            role: ta.role
          })) || [],
          retrocessions: t.transaction_retrocessions?.map(tr => ({
            id: tr.id,
            type: tr.type,
            amount: tr.amount
          })) || [],
          net_fees_without_tax: agencyNetFees
        };
      });

      setTransactions(formattedData);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadTransactions();
    }
  }, [user?.id, dateRange.startDate, dateRange.endDate, selectedAgencies.join(',')]);

  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filter by status
    filtered = filtered.filter(t => statusFilter[t.status]);

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => 
        transaction.property_number.toLowerCase().includes(searchLower) ||
        transaction.agencies.some(agency => agency.toLowerCase().includes(searchLower)) ||
        transaction.negotiators.some(n => n.name.toLowerCase().includes(searchLower)) ||
        transaction.seller.toLowerCase().includes(searchLower) ||
        transaction.buyer.toLowerCase().includes(searchLower)
      );
    }

    // Filter by selected agencies
    if (selectedAgencies.length > 0) {
      filtered = filtered.filter(transaction =>
        transaction.agencies.some(agency => selectedAgencies.includes(agency))
      );
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'property_number':
          comparison = a.property_number.localeCompare(b.property_number);
          break;
        case 'agency':
          comparison = (a.agencies[0] || '').localeCompare(b.agencies[0] || '');
          break;
        case 'negotiator':
          comparison = (a.negotiators[0]?.name || '').localeCompare(b.negotiators[0]?.name || '');
          break;
        case 'seller':
          comparison = (a.seller || '').localeCompare(b.seller || '');
          break;
        case 'buyer':
          comparison = (a.buyer || '').localeCompare(b.buyer || '');
          break;
        case 'sale_price':
          comparison = a.sale_price - b.sale_price;
          break;
        case 'compromise_date':
          // Sort by compromise date, putting empty dates at the end
          if (!a.compromise_date && !b.compromise_date) {
            comparison = 0;
          } else if (!a.compromise_date) {
            comparison = 1; // a comes after b
          } else if (!b.compromise_date) {
            comparison = -1; // b comes after a
          } else {
            comparison = new Date(a.compromise_date).getTime() - new Date(b.compromise_date).getTime();
          }
          break;
        case 'fees_with_tax':
          comparison = (a.fees_with_tax || 0) - (b.fees_with_tax || 0);
          break;
        case 'fees_without_tax':
          comparison = (a.fees_without_tax || 0) - (b.fees_without_tax || 0);
          break;
        case 'net_fees':
          comparison = (a.net_fees_without_tax || 0) - (b.net_fees_without_tax || 0);
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [transactions, searchTerm, sortField, sortDirection, statusFilter, selectedAgencies]);

  useEffect(() => {
    const newStats = {
      totalTransactions: filteredTransactions.length,
      totalSales: filteredTransactions.reduce((sum, t) => sum + t.sale_price, 0),
      totalFees: filteredTransactions.reduce((sum, t) => sum + t.fees_with_tax, 0),
      totalNetFees: filteredTransactions.reduce((sum, t) => sum + t.net_fees_without_tax, 0),
      agencyCount: new Set(filteredTransactions.flatMap(t => t.agencies)).size,
      potentialCount: filteredTransactions.filter(t => t.status === 'potentiel').length,
      compromisCount: filteredTransactions.filter(t => t.status === 'compromis').length,
      acteCount: filteredTransactions.filter(t => t.status === 'acte').length,
      encaisseCount: filteredTransactions.filter(t => t.status === 'encaisse').length,
      clotureCount: filteredTransactions.filter(t => t.status === 'cloture').length
    };
    setStats(newStats);
  }, [filteredTransactions]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return {
    transactions,
    filteredTransactions,
    loading,
    error,
    stats,
    sortField,
    sortDirection,
    handleSort,
    loadTransactions
  };
}